/**
 * Centralized cache key registry for Universal Storage system
 * Provides consistent cache key patterns across all components
 */

export const UNIVERSAL_CACHE_KEYS = {
  // Core Universal Storage caches
  STORAGE_ITEMS: ['universal-storage-items'] as const,
  STORAGE_CATEGORIES: ['universalStorageCategories'] as const,
  STORAGE_CATEGORY_COUNTS: ['universalStorageCategoryCounts'] as const,
  
  // Universal Library caches
  LIBRARY_DATA: ['universal-storage-items'] as const,
  LIBRARY_COUNTS: ['universal-storage-counts'] as const,
  
  // Item-specific caches
  ITEM_CATEGORY: (itemId: string, contentType: string) => 
    ['universalLibraryItemCategory', itemId, contentType] as const,
  
  // Generator-specific caches
  IMAGE_LIBRARY: ['imageLibrary'] as const,
  RESEARCH_LIBRARY_LIST: ['research-library-list'] as const,
  RESEARCH_ITEM_DETAILS: (itemId: string) => 
    ['research-item-details', itemId] as const,
  PRIVATE_RESEARCH_ITEM_DETAILS: (itemId: string) => 
    ['private-research-item-details', itemId] as const,
  WORKBOOK_LIBRARY: ['workbook-library'] as const,
  
  // Content type filters
  FILTERED_STORAGE: (contentTypes?: string[], categoryId?: string | null) => 
    ['universal-storage-items', contentTypes, categoryId] as const,
  FILTERED_LIBRARY: (contentType: string, categoryId?: string | null) => 
    ['universal-storage-items', contentType, categoryId] as const,
} as const;

/**
 * Cache invalidation patterns for different content types
 */
export const CACHE_INVALIDATION_PATTERNS = {
  image: [
    UNIVERSAL_CACHE_KEYS.IMAGE_LIBRARY,
    UNIVERSAL_CACHE_KEYS.LIBRARY_DATA,
    UNIVERSAL_CACHE_KEYS.STORAGE_ITEMS,
  ],
  private_image: [
    UNIVERSAL_CACHE_KEYS.IMAGE_LIBRARY,  // Private images should also refresh image library views
    UNIVERSAL_CACHE_KEYS.LIBRARY_DATA,   // Core universal library data
    UNIVERSAL_CACHE_KEYS.STORAGE_ITEMS,  // Universal storage items list
    UNIVERSAL_CACHE_KEYS.LIBRARY_COUNTS, // Update counts for accurate UI state
    UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORIES, // Categories might be affected
    UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORY_COUNTS, // Category counts need refresh
  ],
  research: [
    UNIVERSAL_CACHE_KEYS.RESEARCH_LIBRARY_LIST,
    UNIVERSAL_CACHE_KEYS.LIBRARY_DATA,
    UNIVERSAL_CACHE_KEYS.STORAGE_ITEMS,
  ],
  private_research: [
    ['private-research-generator'],              // Private research generator queries
    ['private-research-generator-count'],        // Private research count queries
    ['private-research-item-details'],           // Private research item details cache (NEW)
    UNIVERSAL_CACHE_KEYS.LIBRARY_DATA,          // Core universal library data
    UNIVERSAL_CACHE_KEYS.STORAGE_ITEMS,         // Universal storage items list
    UNIVERSAL_CACHE_KEYS.LIBRARY_COUNTS,        // Update counts for accurate UI state
    UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORIES,    // Categories might be affected
    UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORY_COUNTS, // Category counts need refresh
  ],
  workbook: [
    UNIVERSAL_CACHE_KEYS.WORKBOOK_LIBRARY,
    UNIVERSAL_CACHE_KEYS.LIBRARY_DATA,
    UNIVERSAL_CACHE_KEYS.STORAGE_ITEMS,
  ],
} as const;

/**
 * Helper to get all cache keys that should be updated for a content type
 */
export const getCacheKeysForContentType = (contentType: string) => {
  return CACHE_INVALIDATION_PATTERNS[contentType as keyof typeof CACHE_INVALIDATION_PATTERNS] || [
    UNIVERSAL_CACHE_KEYS.LIBRARY_DATA,
    UNIVERSAL_CACHE_KEYS.STORAGE_ITEMS,
  ];
}; 